import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { DataStore } from 'aws-amplify';
import { useTheme } from '@mui/material/styles';
import { DarkUpperSection, LoadingSpinner, PageSection } from '../../../components';
import { useEffect, useMemo, useState } from 'react';
import { useAlert, useCurrentUser, useDatastore } from '../../../hooks';
import { useUserDetails } from '../../../contexts/UserDetailsContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { UserProfileProps } from '../../../datastore/userDetails';
import { UserProfile } from '../../../models';
import { SUPER_ADMINS, UAT_SUPER_ADMINS } from '../../../common/constants';
import { logAdminAction } from '../../../services/auditLogger';
import { AdminActionType } from '../../admin-panel/utils/types';
import { captureError } from '../../../common/sentry';
import { getQmListRoleLabel, QmListRoleValue, toQmListRoleOrNull, toQmListRoleValue } from '../../../common/qmRoles';
import {
  getQmFeatureFlagsRequest,
  listQmSiteInboundCoordinatorSitesRequest,
  QmSiteInboundCoordinatorSiteOption,
  QmSiteInboundSiteUserRole,
  setQmFeatureFlagsRequest
} from '../../../api/qmCrud';

const QM_ROLE_OPTIONS: Array<{ value: QmListRoleValue; label: string }> = [
  { value: 'QM_LIST_READ_ALL', label: getQmListRoleLabel('QM_LIST_READ_ALL') },
  { value: 'QM_LIST_EDIT_ALL', label: getQmListRoleLabel('QM_LIST_EDIT_ALL') }
];

function mapProfileToUser(profile: {
  cognitoID: string;
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  adminPanelAccess?: boolean | null;
  qmListRole?: string | null;
}): UserProfileProps {
  return {
    cognitoID: profile.cognitoID,
    email: profile.email ?? null,
    firstname: profile.firstname ?? null,
    lastname: profile.lastname ?? null,
    adminPanelAccess: profile.adminPanelAccess ?? null,
    qmListRole: toQmListRoleOrNull(profile.qmListRole)
  };
}

const normalise = (value: string | null | undefined) => value?.toLowerCase().trim() ?? '';

function buildSiteInboundRolesFromSites(
  sites: QmSiteInboundCoordinatorSiteOption[],
  profiles: UserProfileProps[]
): QmSiteInboundSiteUserRole[] {
  const profilesByEmail = new Map(profiles.map((profile) => [normalise(profile.email), profile]));
  const roles: QmSiteInboundSiteUserRole[] = [];

  sites.forEach((site) => {
    (site.qmCoordinatorEmails || []).forEach((email) => {
      const normalisedEmail = normalise(email);
      if (!normalisedEmail) return;
      const matchedProfile = profilesByEmail.get(normalisedEmail);
      roles.push({
        siteId: String(site.siteId),
        siteLabel: site.label,
        userEmail: normalisedEmail,
        userCognitoId: matchedProfile?.cognitoID || null,
        role: 'USER'
      });
    });
  });

  return roles;
}

export default function AdminPanelAccessSettings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const store = useDatastore();
  const alert = useAlert();
  const user = useCurrentUser();
  const { userDetails } = useUserDetails();
  const [users, setUsers] = useState<UserProfileProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingUserCognitoIDs, setLoadingUserCognitoIDs] = useState<Set<string>>(new Set());
  const [loadingField, setLoadingField] = useState<{
    cognitoID: string;
    field: 'adminPanelAccess' | 'qmListRole';
  } | null>(null);
  const [isQmFeatureFlagLoading, setIsQmFeatureFlagLoading] = useState(false);
  const [isQmFeatureFlagSaving, setIsQmFeatureFlagSaving] = useState(false);
  const [isQmSiteInboundEnabled, setIsQmSiteInboundEnabled] = useState<boolean>(false);
  const [siteInboundCoordinatorSites, setSiteInboundCoordinatorSites] = useState<QmSiteInboundCoordinatorSiteOption[]>(
    []
  );
  const [siteInboundSiteRoles, setSiteInboundSiteRoles] = useState<QmSiteInboundSiteUserRole[]>([]);
  const [showReasonDialog, setShowReasonDialog] = useState<{
    email: string;
    cognitoID: string;
    adminPanelAccess: boolean;
    reason?: string;
  } | null>(null);
  const [showQmRoleReasonDialog, setShowQmRoleReasonDialog] = useState<{
    email: string;
    cognitoID: string;
    currentRole: QmListRoleValue;
    nextRole: QmListRoleValue;
    reason?: string;
  } | null>(null);
  const [showQmSiteInboundConfirmDialog, setShowQmSiteInboundConfirmDialog] = useState<{
    nextEnabled: boolean;
  } | null>(null);

  const currentUserQmRole = toQmListRoleOrNull(userDetails?.qmListRole);
  const canManageQmGlobalToggle = Boolean(userDetails?.adminPanelAccess) && currentUserQmRole === 'QM_LIST_EDIT_ALL';

  const adminPanelAccessSettingsAccess = useMemo(() => {
    const superAdminList = process.env.REACT_APP_IS_PROD === 'true' ? SUPER_ADMINS : UAT_SUPER_ADMINS;

    return superAdminList.map((sa) => sa.toLowerCase()).includes(user.email.toLowerCase());
  }, [user.email]);

  useEffect(() => {
    async function fetchUsers() {
      try {
        setIsLoading(true);
        const results = await store.userDetails.getAllProfiles();
        if (!results) {
          alert('No users found', 'warning');
          return;
        }
        setUsers(results.sort((a, b) => (a?.email || '').localeCompare(b?.email || '')));
      } catch (error) {
        console.error('Error fetching users:', error);
        captureError(error, 'adminPanelAccessSettings-fetchUsers');
        alert('Error retrieving users', 'error');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUsers();
  }, [alert, store.userDetails]);

  useEffect(() => {
    let isMounted = true;

    async function fetchSiteInboundSiteRoles() {
      try {
        const siteResults = await listQmSiteInboundCoordinatorSitesRequest();
        if (!isMounted) return;
        const sortedSites = siteResults.sort(
          (a, b) => a.label.localeCompare(b.label) || a.siteId.localeCompare(b.siteId, undefined, { numeric: true })
        );
        setSiteInboundCoordinatorSites(sortedSites);
      } catch (error) {
        console.error('Error fetching Site Inbound site roles:', error);
        captureError(error, 'adminPanelAccessSettings-fetchSiteInboundSiteRoles');
        if (isMounted) {
          alert('Unable to load Site Inbound site role settings', 'warning');
        }
      }
    }

    fetchSiteInboundSiteRoles();

    return () => {
      isMounted = false;
    };
  }, [alert]);

  useEffect(() => {
    setSiteInboundSiteRoles(buildSiteInboundRolesFromSites(siteInboundCoordinatorSites, users));
  }, [siteInboundCoordinatorSites, users]);

  useEffect(() => {
    let isMounted = true;

    async function loadQmFeatureFlags() {
      try {
        setIsQmFeatureFlagLoading(true);
        const flags = await getQmFeatureFlagsRequest();
        if (!isMounted) return;
        setIsQmSiteInboundEnabled(flags.qmSiteInboundEnabled);
      } catch (error) {
        console.error('Error loading QM feature flags:', error);
        captureError(error, 'adminPanelAccessSettings-loadQmFeatureFlags');
        if (!isMounted) return;
        alert('Unable to load QM feature toggle. Showing fallback value.', 'warning');
      } finally {
        if (isMounted) {
          setIsQmFeatureFlagLoading(false);
        }
      }
    }

    loadQmFeatureFlags();

    return () => {
      isMounted = false;
    };
  }, [alert]);

  useEffect(() => {
    const subscription = DataStore.observe(UserProfile).subscribe({
      next: ({ opType, element }) => {
        const mappedUser = mapProfileToUser(element);

        setUsers((previousUsers) => {
          if (opType === 'DELETE') {
            const nextUsers = previousUsers.filter((currentUser) => currentUser.cognitoID !== mappedUser.cognitoID);
            return nextUsers.length === previousUsers.length ? previousUsers : nextUsers;
          }

          const existingIndex = previousUsers.findIndex(
            (currentUser) => currentUser.cognitoID === mappedUser.cognitoID
          );
          const nextUsers = [...previousUsers];

          if (existingIndex >= 0) {
            const currentUser = nextUsers[existingIndex];
            const isUnchanged =
              currentUser.email === mappedUser.email &&
              currentUser.firstname === mappedUser.firstname &&
              currentUser.lastname === mappedUser.lastname &&
              currentUser.adminPanelAccess === mappedUser.adminPanelAccess &&
              currentUser.qmListRole === mappedUser.qmListRole;

            if (isUnchanged) {
              return previousUsers;
            }

            nextUsers[existingIndex] = {
              ...currentUser,
              ...mappedUser
            };
          } else {
            nextUsers.push(mappedUser);
          }

          return nextUsers.sort((a, b) => (a?.email || '').localeCompare(b?.email || ''));
        });
      },
      error: (error) => {
        console.error('Admin access settings live update subscription failed:', error);
        captureError(error, 'adminPanelAccessSettings-live-updates');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  function handlePageChange(event: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const changeAdminPanelAccess = async ({
    email,
    cognitoID,
    adminPanelAccess,
    reason
  }: {
    email: string;
    cognitoID: string;
    adminPanelAccess: boolean;
    reason?: string;
  }): Promise<void> => {
    try {
      setLoadingUserCognitoIDs((prev) => new Set(prev).add(cognitoID));
      setLoadingField({ cognitoID, field: 'adminPanelAccess' });
      try {
        await store.userDetails.updateAdminPanelAccess({ cognitoID, adminPanelAccess });

        setUsers((prev) => prev.map((user) => (user.cognitoID === cognitoID ? { ...user, adminPanelAccess } : user)));
      } catch (error) {
        throw error;
      }

      try {
        await logAdminAction({
          actionType: adminPanelAccess
            ? AdminActionType.ADMIN_PANEL_GRANT_ACCESS
            : AdminActionType.ADMIN_PANEL_REVOKE_ACCESS,
          beforeAction: { email, adminAccess: !adminPanelAccess },
          afterAction: { email, adminAccess: adminPanelAccess },
          actionBy: user.email,
          reason
        });
      } catch (auditError) {
        console.error('Failed to log admin action:', auditError);
        captureError(auditError, 'adminPanelAccessSettings-changeAdminPanelAccess-auditLog');
        alert('Access updated but audit log failed.', 'warning');
      }

      alert(`${adminPanelAccess ? 'Granted access.' : 'Revoked access.'}`, 'success');
    } catch (error) {
      console.error('Error updating user admin panel access:', error);
      captureError(error, 'adminPanelAccessSettings-changeAdminPanelAccess');
      alert('Error updating user admin panel access', 'error');
    } finally {
      setLoadingUserCognitoIDs((prev) => {
        const next = new Set(prev);
        next.delete(cognitoID);
        return next;
      });
      setLoadingField(null);

      setShowReasonDialog(null);
    }
  };

  const updateQmRole = async ({
    email,
    cognitoID,
    currentRole,
    nextRole,
    reason
  }: {
    email: string;
    cognitoID: string;
    currentRole: QmListRoleValue;
    nextRole: QmListRoleValue;
    reason?: string;
  }): Promise<void> => {
    try {
      setLoadingUserCognitoIDs((prev) => new Set(prev).add(cognitoID));
      setLoadingField({ cognitoID, field: 'qmListRole' });

      await store.userDetails.updateQmListRole({ cognitoID, qmListRole: nextRole });
      setUsers((prev) =>
        prev.map((currentUser) =>
          currentUser.cognitoID === cognitoID ? { ...currentUser, qmListRole: nextRole } : currentUser
        )
      );

      try {
        await logAdminAction({
          actionType:
            nextRole === 'QM_LIST_EDIT_ALL'
              ? AdminActionType.QM_ROLE_SET_EDITABLE
              : nextRole === 'QM_LIST_READ_ALL'
                ? AdminActionType.QM_ROLE_SET_READ_ONLY
                : AdminActionType.QM_ROLE_SET_NO_ACCESS,
          beforeAction: { email, qmListRole: currentRole },
          afterAction: { email, qmListRole: nextRole },
          actionBy: user.email,
          reason
        });
      } catch (auditError) {
        console.error('Failed to log QM role action:', auditError);
        captureError(auditError, 'adminPanelAccessSettings-updateQmRole-auditLog');
        alert('Role updated but audit log failed.', 'warning');
      }

      alert('QM dashboard role updated.', 'success');
    } catch (error) {
      console.error('Error updating QM dashboard role:', error);
      captureError(error, 'adminPanelAccessSettings-updateQmRole');
      alert('Error updating QM dashboard role', 'error');
    } finally {
      setLoadingUserCognitoIDs((prev) => {
        const next = new Set(prev);
        next.delete(cognitoID);
        return next;
      });
      setLoadingField(null);

      setShowQmRoleReasonDialog(null);
    }
  };

  const getSiteRolesForUser = (selectedUser: UserProfileProps) => {
    const userEmail = normalise(selectedUser.email);
    return siteInboundSiteRoles.filter(
      (role) =>
        (selectedUser.cognitoID && role.userCognitoId === selectedUser.cognitoID) ||
        normalise(role.userEmail) === userEmail
    );
  };

  const updateQmSiteInboundFeatureFlag = async (enabled: boolean): Promise<void> => {
    try {
      setIsQmFeatureFlagSaving(true);
      const previousValue = isQmSiteInboundEnabled;
      const updated = await setQmFeatureFlagsRequest({
        qmNewDashboardEnabled: true,
        qmSiteInboundEnabled: enabled
      });
      setIsQmSiteInboundEnabled(updated.qmSiteInboundEnabled);

      try {
        await logAdminAction({
          actionType: updated.qmSiteInboundEnabled
            ? AdminActionType.QM_SITE_INBOUND_ENABLE
            : AdminActionType.QM_SITE_INBOUND_DISABLE,
          beforeAction: {
            target: 'QM Site Inbound Global Toggle',
            qmSiteInboundEnabled: previousValue
          },
          afterAction: {
            target: 'QM Site Inbound Global Toggle',
            qmSiteInboundEnabled: updated.qmSiteInboundEnabled
          },
          actionBy: user.email
        });
      } catch (auditError) {
        console.error('Failed to log QM site inbound toggle action:', auditError);
        captureError(auditError, 'adminPanelAccessSettings-updateQmSiteInboundFeatureFlag-auditLog');
        alert('QM Site Inbound toggle updated but audit log failed.', 'warning');
      }

      alert(`QM Site Inbound feature ${updated.qmSiteInboundEnabled ? 'enabled' : 'disabled'}.`, 'success');
    } catch (error) {
      console.error('Error updating QM Site Inbound feature toggle:', error);
      captureError(error, 'adminPanelAccessSettings-updateQmSiteInboundFeatureFlag');
      const errorMessage = String(
        (error as any)?.errors?.[0]?.message || (error as any)?.message || error
      ).toLowerCase();
      if (errorMessage.includes('unauthorized')) {
        alert('You need Admin Panel Access and QM Role "Write All" to update this global toggle.', 'warning');
      } else {
        alert('Error updating QM Site Inbound feature toggle', 'error');
      }
    } finally {
      setIsQmFeatureFlagSaving(false);
    }
  };

  const filteredUsers = users
    .filter((user) => user.email)
    .filter((user) => {
      if (!filterText) return true;

      const search = normalise(filterText);

      return (
        normalise(user.firstname).includes(search) ||
        normalise(user.lastname).includes(search) ||
        normalise(user.email).includes(search)
      );
    });
  const PAGE_SIZE = 25;
  const totalNumberOfPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (!adminPanelAccessSettingsAccess) {
    return null;
  }

  return (
    <>
      {showReasonDialog ? (
        <Dialog
          open={!!showReasonDialog}
          onClose={() => setShowReasonDialog(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
          <DialogTitle
            sx={{
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              px: 3,
              py: 2.5,
              backgroundColor: '#f9fafb'
            }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {showReasonDialog?.adminPanelAccess ? 'Grant' : 'Revoke'} Admin Access
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                mb: 1.5,
                display: 'block'
              }}>
              Email
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                    {showReasonDialog.email}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                {`Reason for ${showReasonDialog?.adminPanelAccess ? 'Granting' : 'Revoking'} access`}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={showReasonDialog?.reason ?? ''}
                onChange={(e) =>
                  setShowReasonDialog((prev) => {
                    if (!prev) {
                      return prev;
                    }
                    return { ...prev, reason: e.target.value || '' };
                  })
                }
                placeholder=""
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              backgroundColor: '#fafafa',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
            <Button
              onClick={() => {
                setShowReasonDialog(null);
              }}
              disabled={loadingUserCognitoIDs.has(showReasonDialog.cognitoID)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                changeAdminPanelAccess(showReasonDialog);
              }}
              disabled={!showReasonDialog?.reason?.trim() || loadingUserCognitoIDs.has(showReasonDialog.cognitoID)}
              sx={{
                px: 2,
                py: 0.75,
                fontWeight: 500,
                fontSize: '0.8rem',
                textTransform: 'none',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                boxShadow: 'none',
                borderRadius: 1
              }}>
              {loadingUserCognitoIDs.has(showReasonDialog.cognitoID) ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <></>
      )}

      {showQmRoleReasonDialog ? (
        <Dialog
          open={!!showQmRoleReasonDialog}
          onClose={() => setShowQmRoleReasonDialog(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
          <DialogTitle
            sx={{
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              px: 3,
              py: 2.5,
              backgroundColor: '#f9fafb'
            }}>
            <Typography variant="body1" fontWeight={600}>
              Update QM Dashboard Role
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
              Email
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)',
                mt: 1.5
              }}>
              <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                {showQmRoleReasonDialog.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {getQmListRoleLabel(showQmRoleReasonDialog.currentRole)} →{' '}
                {getQmListRoleLabel(showQmRoleReasonDialog.nextRole)}
              </Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                Reason for Role Change
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={showQmRoleReasonDialog.reason ?? ''}
                onChange={(e) =>
                  setShowQmRoleReasonDialog((prev) => {
                    if (!prev) return prev;
                    return { ...prev, reason: e.target.value || '' };
                  })
                }
                helperText="This reason will be logged in the audit trail"
                sx={{ mt: 1.5 }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              backgroundColor: '#fafafa',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
            <Button
              onClick={() => setShowQmRoleReasonDialog(null)}
              disabled={loadingUserCognitoIDs.has(showQmRoleReasonDialog.cognitoID)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => updateQmRole(showQmRoleReasonDialog)}
              disabled={
                !showQmRoleReasonDialog.reason?.trim() || loadingUserCognitoIDs.has(showQmRoleReasonDialog.cognitoID)
              }>
              {loadingUserCognitoIDs.has(showQmRoleReasonDialog.cognitoID) ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <></>
      )}

      {showQmSiteInboundConfirmDialog ? (
        <Dialog
          open={!!showQmSiteInboundConfirmDialog}
          onClose={() => {
            if (!isQmFeatureFlagSaving) {
              setShowQmSiteInboundConfirmDialog(null);
            }
          }}
          maxWidth="xs"
          fullWidth>
          <DialogTitle>Confirm QM Site Inbound Toggle</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This change affects all users.
              </Typography>
              <Typography variant="body2">
                Current: <strong>{isQmSiteInboundEnabled ? 'Enabled' : 'Disabled'}</strong>
              </Typography>
              <Typography variant="body2">
                New: <strong>{showQmSiteInboundConfirmDialog.nextEnabled ? 'Enabled' : 'Disabled'}</strong>
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowQmSiteInboundConfirmDialog(null)}
              disabled={isQmFeatureFlagSaving || isQmFeatureFlagLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await updateQmSiteInboundFeatureFlag(showQmSiteInboundConfirmDialog.nextEnabled);
                setShowQmSiteInboundConfirmDialog(null);
              }}
              disabled={isQmFeatureFlagSaving || isQmFeatureFlagLoading}>
              {isQmFeatureFlagSaving ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <></>
      )}

      <DarkUpperSection>
        <Stack spacing={4} sx={{ padding: '1em 0' }}>
          <Stack direction="row" alignItems="center">
            <IconButton onClick={() => navigate('/settings')}>
              <ArrowBackIcon fontSize="large" sx={{ color: theme.palette.primary.contrastText }} />
            </IconButton>
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Settings
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <Typography variant="h4" color={theme.palette.primary.contrastText}>
              Modify User Access Settings
            </Typography>
          </Stack>
        </Stack>
      </DarkUpperSection>
      <LoadingSpinner isLoading={isLoading}>
        <PageSection>
          <Stack spacing={4} sx={{ padding: '2em 0' }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              sx={{
                p: 2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper
              }}>
              <Stack spacing={0.5}>
                <Typography variant="h6">QM Site Inbound Global Toggle</Typography>
                <Typography variant="body2" color="text.secondary">
                  Controls the visibility of the Site Inbound QM type in the Raise a QM flow for all users.
                </Typography>
                {!canManageQmGlobalToggle && (
                  <Typography variant="caption" color="warning.main">
                    Read only: you need Admin Panel Access and QM Role "Write All" to update this global toggle.
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {isQmFeatureFlagLoading && <CircularProgress size={18} />}
                <FormControlLabel
                  control={
                    <Switch
                      checked={isQmSiteInboundEnabled}
                      disabled={isQmFeatureFlagLoading || isQmFeatureFlagSaving || !canManageQmGlobalToggle}
                      onChange={(event) =>
                        setShowQmSiteInboundConfirmDialog({
                          nextEnabled: event.target.checked
                        })
                      }
                    />
                  }
                  label={isQmSiteInboundEnabled ? 'Enabled' : 'Disabled'}
                />
              </Stack>
            </Stack>
            <Stack>
              <TextField
                aria-label="Filter"
                label="Filter"
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setCurrentPage(1);
                }}
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
            </Stack>
          </Stack>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    First Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Last Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Email
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Admin Panel Access
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    QM Dashboard Role
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 280 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Site Inbound QM Site Specific Coordinators
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Applies only to Site Inbound QMs for Cycle Count in Progress.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No users found matching "{filterText}"
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {paginatedUsers.map((u) => (
                <TableRow key={u.cognitoID}>
                  <TableCell>{u.firstname}</TableCell>
                  <TableCell>{u.lastname}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        aria-label="Toggle admin panel access"
                        checked={!!u.adminPanelAccess}
                        disabled={loadingUserCognitoIDs.has(u.cognitoID)}
                        onChange={(e) =>
                          setShowReasonDialog({
                            email: u.email || '',
                            cognitoID: u.cognitoID,
                            adminPanelAccess: e.target.checked,
                            reason: ''
                          })
                        }
                        color="primary"
                      />

                      {loadingField?.cognitoID === u.cognitoID && loadingField.field === 'adminPanelAccess' && (
                        <CircularProgress size={16} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        size="small"
                        value={toQmListRoleValue(u.qmListRole)}
                        disabled={loadingUserCognitoIDs.has(u.cognitoID)}
                        onChange={(e) => {
                          const currentRole = toQmListRoleValue(u.qmListRole);
                          const nextRole = e.target.value as QmListRoleValue;
                          if (nextRole === currentRole) return;
                          setShowQmRoleReasonDialog({
                            email: u.email || '',
                            cognitoID: u.cognitoID,
                            currentRole,
                            nextRole,
                            reason: ''
                          });
                        }}>
                        {QM_ROLE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      {loadingField?.cognitoID === u.cognitoID && loadingField.field === 'qmListRole' && (
                        <CircularProgress size={16} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {getSiteRolesForUser(u).length > 0 ? (
                          getSiteRolesForUser(u).map((role) => {
                            return (
                              <Chip
                                key={`${u.cognitoID}-${role.siteId}`}
                                label={role.siteLabel || role.siteId}
                                size="small"
                                variant="outlined"
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack spacing={2} direction="row" justifyContent="center" sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
            <Pagination count={totalNumberOfPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Stack>
        </PageSection>
      </LoadingSpinner>
    </>
  );
}
